import { FieldPath, UseFormReturn, UseFormSetError } from "react-hook-form"
import {
  CalendarDurationType,
  CountryIntelligence,
  CountryIntelligenceRulesRestrictiveCovenants,
  CountryIsoCode,
  Employment,
  Engagement,
  PostTerminationPaymentAmountType,
  PostTerminationPaymentFrequency,
  PostTerminationRestrictedArea,
  RestrictiveCovenants,
} from "@oysterhr/schema"

export enum NonSolicitationOptionsEnum {
  Employees = "employees",
  Customers = "customers",
  Both = "both",
}

export type DurationOption = "custom" | `${number}_${CalendarDurationType}`

export type StringifiedBoolean = "true" | "false" | "null"

export type PostTerminationSection = {
  restrictedArea?: PostTerminationRestrictedArea
  durationOption?: DurationOption
  durationCustom?: number
  areaOfBusiness?: string
  /** Needed only in staff view */
  areaOfBusinessInLocalLanguage?: string
  amountType?: PostTerminationPaymentAmountType
  amountFixed?: number | null
  amountPercentage?: number | null
  paymentFrequency?: PostTerminationPaymentFrequency
  acknowledged?: boolean
  additionalAreas?: CountryIsoCode[]
}
export type RestrictiveCovenantsFormValues = {
  postTerminationAreaOfBusiness: string
  postTerminationAreaOfBusinessInLocalLanguage: string
  confidentiality: StringifiedBoolean
  nonCompetition: StringifiedBoolean
  nonSolicitation: StringifiedBoolean
  nonSolicitationOption?: NonSolicitationOptionsEnum
  postTerminationSection?: PostTerminationSection
}

export type RestrictiveCovenantsProps = {
  employmentId: string
  countryCode: string
  subdivisionCode?: string | null
  booleanFieldType?: BooleanFieldType
  initialValuesFunction: (
    employment: EmploymentRestrictiveCovenants,
  ) => RestrictiveCovenantsFormValues
  onSubmit: (
    values: RestrictiveCovenantsFormValues,
    setError: UseFormSetError<RestrictiveCovenantsFormValues & { custom?: string }>,
  ) => void
  formRef?: React.RefObject<HTMLFormElement>
  /**
   * The name of the fields we want to disable
   */
  disabledFields?: FieldPath<RestrictiveCovenantsFormValues>[]
  hiddenFields?: FieldPath<RestrictiveCovenantsFormValues>[]
  /**
   * We need engagement to be able to know the langauge that this engagement has
   * This will be used to allow staff users to automatically translate the area of business
   */
  engagement?: Engagement
}
export type RestrictiveCovenantsSectionChildProps = {
  form: UseFormReturn<RestrictiveCovenantsFormValues>
}

export type ClientPaymentType = Pick<
  PostTerminationSection,
  "amountFixed" | "amountPercentage" | "amountType" | "paymentFrequency"
>
export type ServerPaymentType = Pick<
  RestrictiveCovenants,
  | "postTerminationAmountAbsoluteSubunit"
  | "postTerminationAmountPercentage"
  | "postTerminationPaymentFrequency"
>

export type EmploymentRestrictiveCovenants = Pick<
  Employment,
  "salaryAmount" | "salaryCurrencyCode" | "restrictiveCovenants" | "countryCode" | "subdivisionCode"
>

export type BooleanFieldType = "toggle" | "select" | "radio"

export type RestrictiveCovenantsKnowledgeBase =
  Partial<CountryIntelligenceRulesRestrictiveCovenants>

export type AreaOfBusinessKnowledgeBase = Pick<
  CountryIntelligence,
  "companyAreaOfBusinessFieldRequired" | "companyAreaOfBusinessFieldForSubdivisionCodes"
>
